import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { PersistentNotification, ToastAction, ToastNotification } from './types';
import { NotificationPosition } from '@/feature/notifications/components/toast_with_progress';
import { clearDataMigration, CURRENT_APP_VERSION } from '@/utils/zustand_helpers';
import { DEFAULT_TOAST_DURATION, ToastDurationOption, TOAST_DURATION_OPTIONS } from '@/common/timeout_constants';

export const MAX_PERSISTED_NOTIFICATIONS = 100;
export const DEFAULT_NOTIFICATION_POSITION: NotificationPosition = 'top-right';
export const DEFAULT_SHOW_TOASTS = true;

export type NotifyOptions = {
  manualDismiss?: boolean;
  waiting?: boolean;
  debugInfo?: string;
  replaceKey?: string;
  accountId?: string | number;
  action?: ToastAction;
  secondaryAction?: ToastAction;
  criticalOverride?: boolean;
};

type NotificationState = {
  list: PersistentNotification[];
  position: NotificationPosition;
  showToasts: boolean;
  toastDuration: ToastDurationOption;
  // actions
  addToast: (message: string, severity: ToastNotification['severity'], options?: NotifyOptions) => void;
  markRead: (id: string) => void;
  markManyRead: (ids: string[]) => void;
  markAllRead: () => void;
  clear: () => void;
  setPosition: (position: NotificationPosition) => void;
  setShowToasts: (show: boolean) => void;
  setToastDuration: (duration: ToastDurationOption) => void;
  resetPreferences: () => void;
};

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      list: [],
      position: DEFAULT_NOTIFICATION_POSITION,
      showToasts: DEFAULT_SHOW_TOASTS,
      toastDuration: DEFAULT_TOAST_DURATION,

      addToast: (message: string, severity: ToastNotification['severity'], options?: NotifyOptions) => {
        const {
          manualDismiss = false,
          waiting = false,
          debugInfo,
          replaceKey,
          accountId,
          action,
          secondaryAction,
          criticalOverride,
        } = options || {};

        const sessionId = typeof window !== 'undefined' ? window.xBrowserSessionId : undefined;
        const defaultAccountId = typeof window !== 'undefined' ? window.dexAccountId : undefined;
        const newToast: ToastNotification = {
          id: crypto.randomUUID(),
          message,
          severity,
          manualDismiss,
          waiting,
          debugInfo,
          sessionId,
          accountId: accountId ?? defaultAccountId,
          replaceKey,
          action,
          secondaryAction,
          criticalOverride,
        };

        set((state) => {
          const persistentNotification: PersistentNotification = {
            ...newToast,
            created: Date.now(),
            read: false,
            superseded: false,
          };

          // Remove waiting notifications from history (temporary loading states like "Confirming...")
          let filteredList = state.list.filter((notification) => !notification.waiting);

          // Mark previous notifications with same replaceKey as superseded (keeps in history, hides from toast)
          if (replaceKey) {
            filteredList = filteredList.map((notification) => {
              if (notification.replaceKey === replaceKey && !notification.superseded) {
                return { ...notification, superseded: true };
              }
              return notification;
            });
          }

          const newList = [persistentNotification, ...filteredList].slice(0, MAX_PERSISTED_NOTIFICATIONS);

          return {
            list: newList,
          };
        });
      },

      markRead: (id) =>
        set((s) => ({
          list: s.list.map((n) => (n.id === id ? { ...n, read: true } : n)),
        })),

      markManyRead: (ids) =>
        set((s) => {
          if (!ids.length) {
            return {};
          }

          const idSet = new Set(ids);
          let hasChanges = false;

          const updatedList = s.list.map((notification) => {
            if (idSet.has(notification.id) && !notification.read) {
              hasChanges = true;
              return { ...notification, read: true };
            }
            return notification;
          });

          return hasChanges ? { list: updatedList } : {};
        }),

      markAllRead: () =>
        set((s) => ({
          list: s.list.map((n) => (n.read ? n : { ...n, read: true })),
        })),

      clear: () => set({ list: [] }),

      setPosition: (position) => set({ position }),

      setShowToasts: (show) => set({ showToasts: show }),
      setToastDuration: (duration) => set({ toastDuration: duration }),
      resetPreferences: () =>
        set({
          position: DEFAULT_NOTIFICATION_POSITION,
          showToasts: DEFAULT_SHOW_TOASTS,
          toastDuration: DEFAULT_TOAST_DURATION,
        }),
    }),
    {
      name: 'dex-notifications',
      version: CURRENT_APP_VERSION,
      migrate: clearDataMigration,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        list: state.list.slice(0, MAX_PERSISTED_NOTIFICATIONS),
        position: state.position,
        showToasts: state.showToasts,
        toastDuration: state.toastDuration,
      }),
    }
  )
);

// Helpers
export function useNotify() {
  return useNotificationStore((state) => state.addToast);
}

export const notify = (message: string, severity: 'success' | 'info' | 'warning' | 'error', options?: NotifyOptions) =>
  useNotificationStore.getState().addToast(message, severity, options);
